// src/components/ForgotPasswordModal.js
import React, { useState, useEffect, useRef } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import './ForgotPasswordModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';

const ForgotPasswordModal = ({ currentLanguage, getLocalizedText, onClose }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const modalRef = useRef(null);

  const t = (ar, en) => getLocalizedText(ar, en);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);
      setIsSent(true);
    } catch (err) {
      let errorMsg;
      if (currentLanguage === 'ar') {
        switch (err.code) {
          case 'auth/invalid-email':
            errorMsg = 'البريد الإلكتروني غير صالح.';
            break;
          case 'auth/too-many-requests':
            errorMsg = 'تم إرسال طلبات كثيرة جدًا. حاول مرة أخرى بعد قليل.';
            break;
          default:
            errorMsg = 'حدث خطأ أثناء إرسال الرابط. حاول مرة أخرى.';
        }
      } else {
        switch (err.code) {
          case 'auth/invalid-email':
            errorMsg = 'Invalid email address.';
            break;
          case 'auth/too-many-requests':
            errorMsg = 'Too many requests. Please try again later.';
            break;
          default:
            errorMsg = 'An error occurred. Please try again.';
        }
      }
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const isValidEmail = email.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // شاشة النجاح بعد الإرسال (بدون لوجو)
 // شاشة النجاح (نظيفة جدًا دلوقتي)
if (isSent) {
  return (
    <div className="forgot-password-modal-overlay">
      <div className={`forgot-password-modal success-mode ${currentLanguage === 'ar' ? 'ar' : 'en'}`} ref={modalRef}>
        <button className="forgot-password-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <FontAwesomeIcon icon={faCheckCircle} className="forgot-password-success-icon" />

        <p className="forgot-password-success-message">
          {t(
            'إذا كان هناك حساب مرتبط بهذا البريد، فقد تم إرسال رابط إعادة تعيين كلمة المرور. تحقق من بريدك الوارد ومجلد السبام.',
            'If there is an existing account linked to this email, a password reset link has been sent. Please check your inbox and spam folder.'
          )}
        </p>
      </div>
    </div>
  );
}

  // شاشة إدخال الإيميل (مع اللوجو عادي)
  return (
    <div className="forgot-password-modal-overlay">
      <div className={`forgot-password-modal ${currentLanguage === 'ar' ? 'ar' : 'en'}`} ref={modalRef}>
        <button className="forgot-password-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <div className="forgot-password-modal-logo">
          <img src="/assets/logo.png" alt="ROOMATE" />
        </div>

        <h2 className="forgot-password-modal-title">
          {t('نسيت كلمة المرور؟', 'Forgot your password?')}
        </h2>

        <p className="forgot-password-modal-subtitle">
          {t(
            'أدخل بريدك الإلكتروني وسيتم إرسال رابط لإعادة تعيين كلمة المرور.',
            'Enter your email and we will send you a link to reset your password.'
          )}
        </p>

        <form onSubmit={handleSubmit}>
          <div className="forgot-password-modal-field">
            <label>{t('البريد الإلكتروني', 'Email Address')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('أدخل بريدك الإلكتروني', 'Enter your email')}
              dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
              required
            />
          </div>

          {error && <div className="forgot-password-modal-error">{error}</div>}

          <button
            type="submit"
            className={`forgot-password-modal-submit ${isValidEmail && !isLoading ? 'active' : ''}`}
            disabled={!isValidEmail || isLoading}
          >
            {isLoading ? t('جاري الإرسال...', 'Sending...') : t('إرسال الرابط', 'Send Link')}
          </button>
        </form>

        <div className="forgot-password-modal-back">
          <button onClick={onClose} className="forgot-password-modal-back-link">
            {t('العودة إلى تسجيل الدخول', 'Back to Login')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;